import * as classnames from "classnames";
import * as Clipboard from "clipboard";
import { isEqual } from "lodash";
import * as React from "react";
import { generate } from "shortid";

import { Column, Icon, Row, TableBody, Msg } from "../../../shared/components";
import Message from "../../../shared/services/message";

/**
 * Prop interface
 */
export interface SystemHealthItemProps {
  item: {
    status: boolean;
    titleMessageKey: string;
    descriptionMessageKey: string;
    resultText: string;
    resultDetailText?: string;
  };
}

/**
 * State interface
 */
export interface SystemHealthItemState {
  copied: boolean;
}

/**
 * Page component
 */
class SystemHealthItem extends React.Component<
  SystemHealthItemProps,
  SystemHealthItemState
> {
  private clipboard: Clipboard;
  readonly id: string;

  constructor(props: SystemHealthItemProps) {
    super(props);
    this.state = { copied: false };
    this.id = `copybox${generate()}`;
  }

  public shouldComponentUpdate(
    nextProps: SystemHealthItemProps,
    nextState: SystemHealthItemState,
  ) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public componentDidUpdate() {
    this.clipboard = new Clipboard(".code-system-health-item__copy");
  }

  public componentWillUnmount() {
    if (this.clipboard) {
      this.clipboard.destroy();
    }
  }

  public render() {
    const {
      status,
      titleMessageKey,
      descriptionMessageKey,
      resultText,
      resultDetailText,
    } = this.props.item;
    const { copied } = this.state;

    const rootStyle = classnames("code-system-health-item", {
      "is-poor": !status,
    });

    const copyButtonStyle = classnames(
      "code-system-health-item__copy",
      "button",
      "is-small",
      {
        "is-copied": copied,
      },
    );

    return (
      <TableBody className={rootStyle}>
        <Row className="code-system-health-item__title">
          <Column>
            <Msg id={titleMessageKey} />
          </Column>
          <Column>{resultText}</Column>
          <Column>
            <Icon
              className="code-system-health-item__status-icon"
              type={status ? "check" : "exclamation-triangle"}
            />
          </Column>
          <Column>
            <Msg id={status ? "health.noProblem" : descriptionMessageKey} />
          </Column>
        </Row>
        {Boolean(resultDetailText) && (
          <Row className="code-system-health-item__error-detail">
            <Column colSpan={4}>
              <span id={this.id}>{resultDetailText}</span>
              <button
                className={copyButtonStyle}
                data-clipboard-text={resultDetailText}
                data-badge={Message.getMessageByKey("message.copied")}
                onClick={this.copy}
              >
                <Icon type="clipboard" />
              </button>
            </Column>
          </Row>
        )}
      </TableBody>
    );
  }

  private copy = (e: React.FormEvent<HTMLButtonElement>) => {
    this.setState({ copied: true });
    window.setTimeout(() => this.setState({ copied: false }), 1000);
  };
}

export default SystemHealthItem;
